import { BALANCE } from '../balance';
import type { AchievementDefInput } from '../schemas/progressionDefs';

/**
 * The 12 MVP achievements (content-schemas §16.2, economy-progression §8): 5 bronze, 4 silver, 3 gold = 37 stars,
 * plus two for Studio NANO (studio-nano.md §8): 7 more.
 * Stars per tier come from BALANCE.starsPer.achievement.
 */
const S = BALANCE.starsPer.achievement;
const bronze = { type: 'stars', amount: S.bronze } as const;
const silver = { type: 'stars', amount: S.silver } as const;
const gold = { type: 'stars', amount: S.gold } as const;

export const ACHIEVEMENTS = [
  {
    id: 'ach_first_place',
    category: 'organizing',
    tier: 'bronze',
    nameKey: 'ach.ach_first_place.name',
    descKey: 'ach.ach_first_place.desc',
    progress: { kind: 'counter', stat: 'itemsPlaced', target: 1 },
    rewards: [bronze, { type: 'coins', amount: 25 }],
    order: 1,
  },
  {
    id: 'ach_neat_20',
    category: 'organizing',
    tier: 'silver',
    nameKey: 'ach.ach_neat_20.name',
    descKey: 'ach.ach_neat_20.desc',
    progress: { kind: 'counter', stat: 'itemsPlaced', target: 20 },
    rewards: [silver, { type: 'coins', amount: 50 }],
    order: 2,
  },
  {
    id: 'ach_right_spot_15',
    category: 'organizing',
    tier: 'gold',
    nameKey: 'ach.ach_right_spot_15.name',
    descKey: 'ach.ach_right_spot_15.desc',
    progress: { kind: 'distinct', stat: 'rightSpotItemIds', target: 15 },
    rewards: [gold, { type: 'badge', badgeId: 'bdg_right_spot' }],
    order: 3,
  },
  {
    id: 'ach_first_sale',
    category: 'shopkeeping',
    tier: 'bronze',
    nameKey: 'ach.ach_first_sale.name',
    descKey: 'ach.ach_first_sale.desc',
    progress: { kind: 'counter', stat: 'unitsSold', target: 1 },
    rewards: [bronze, { type: 'coins', amount: 35 }],
    order: 4,
  },
  {
    id: 'ach_seller_50',
    category: 'shopkeeping',
    tier: 'silver',
    nameKey: 'ach.ach_seller_50.name',
    descKey: 'ach.ach_seller_50.desc',
    progress: { kind: 'counter', stat: 'unitsSold', target: 50 },
    rewards: [silver, { type: 'coins', amount: 80 }, { type: 'badge', badgeId: 'bdg_seller_50' }],
    order: 5,
  },
  {
    id: 'ach_orders_10',
    category: 'shopkeeping',
    tier: 'gold',
    nameKey: 'ach.ach_orders_10.name',
    descKey: 'ach.ach_orders_10.desc',
    progress: { kind: 'counter', stat: 'ordersFulfilled', target: 10 },
    rewards: [gold, { type: 'title', titleId: 'ttl_orders_10' }],
    order: 6,
  },
  {
    id: 'ach_decor_10',
    category: 'decorating',
    tier: 'silver',
    nameKey: 'ach.ach_decor_10.name',
    descKey: 'ach.ach_decor_10.desc',
    progress: { kind: 'distinct', stat: 'ownedDecorFurnitureIds', target: 10 },
    rewards: [silver, { type: 'badge', badgeId: 'bdg_decor_10' }],
    order: 7,
  },
  {
    id: 'ach_cozy_home_3',
    category: 'decorating',
    tier: 'gold',
    nameKey: 'ach.ach_cozy_home_3.name',
    descKey: 'ach.ach_cozy_home_3.desc',
    progress: { kind: 'condition', condition: { type: 'cozinessAtLeast', roomId: 'home', level: 3 } },
    rewards: [gold, { type: 'photoFrame', frameId: 'frm_cozy_home' }],
    order: 8,
  },
  {
    id: 'ach_hello_3',
    category: 'social',
    tier: 'bronze',
    nameKey: 'ach.ach_hello_3.name',
    descKey: 'ach.ach_hello_3.desc',
    // npcsTalkedTo only counts player-initiated talks with Lan/Bảo/Mít (content-schemas §12.3).
    progress: { kind: 'distinct', stat: 'npcsTalkedTo', target: 3 },
    rewards: [bronze, { type: 'avatarFrame', frameId: 'afr_neighbor' }],
    order: 9,
  },
  {
    id: 'ach_cat_boss',
    category: 'social',
    tier: 'bronze',
    nameKey: 'ach.ach_cat_boss.name',
    descKey: 'ach.ach_cat_boss.desc',
    // The mysterious description is story-dialogue §8.5 `cat.ach_secret.hint`.
    hintKey: 'cat.ach_secret.hint',
    secret: true,
    progress: { kind: 'counter', stat: 'catPets', target: 20 },
    rewards: [bronze, { type: 'title', titleId: 'ttl_cat_secretary' }],
    order: 10,
  },
  {
    id: 'ach_first_photo',
    category: 'moments',
    tier: 'bronze',
    nameKey: 'ach.ach_first_photo.name',
    descKey: 'ach.ach_first_photo.desc',
    progress: { kind: 'counter', stat: 'photosTaken', target: 1 },
    rewards: [
      bronze,
      { type: 'sticker', stickerId: 'stk_photo_1' },
      { type: 'sticker', stickerId: 'stk_photo_2' },
      { type: 'sticker', stickerId: 'stk_photo_3' },
    ],
    order: 11,
  },
  {
    id: 'ach_set_complete',
    category: 'moments',
    tier: 'silver',
    nameKey: 'ach.ach_set_complete.name',
    descKey: 'ach.ach_set_complete.desc',
    progress: { kind: 'distinct', stat: 'setsCompleted', target: 1 },
    rewards: [silver, { type: 'badge', badgeId: 'bdg_set_complete' }],
    order: 12,
  },
  // Studio NANO (studio-nano.md §8).
  {
    id: 'ach_first_shoot',
    category: 'moments',
    tier: 'bronze',
    nameKey: 'ach.ach_first_shoot.name',
    descKey: 'ach.ach_first_shoot.desc',
    progress: { kind: 'counter', stat: 'shootsCompleted', target: 1 },
    rewards: [bronze, { type: 'coins', amount: 40 }],
    order: 13,
  },
  {
    id: 'ach_shoot_excellent_10',
    category: 'moments',
    tier: 'gold',
    nameKey: 'ach.ach_shoot_excellent_10.name',
    descKey: 'ach.ach_shoot_excellent_10.desc',
    progress: { kind: 'counter', stat: 'shootsExcellent', target: 10 },
    rewards: [gold, { type: 'title', titleId: 'ttl_shoot_master' }],
    order: 14,
  },
] as const satisfies readonly AchievementDefInput[];
